import { groq } from 'next-sanity';
import { z } from 'zod';

import { image, SanityImage } from './image';
import { link, SanityLink } from './link';
import { SanityIcon } from './media';

export const SanityMenuLinkGroup = z.object({
  _type: z.literal('linkGroup'),
  _key: z.string(),
  title: z.string(),
  icon: SanityImage.optional().nullable(),
  libraryIcon: SanityIcon.optional().nullable(),
  description: z.string().optional().nullable(),
  items: z.array(
    z.object({
      _key: z.string(),
      link: SanityLink,
      description: z.string().optional().nullable(),
      style: z.enum(['default', 'purpleArrow']).optional().nullable(),
      icon: SanityImage.optional().nullable(),
    }),
  ),
});
export type SanityMenuLinkGroup = z.infer<typeof SanityMenuLinkGroup>;

export const SanityMenuFeaturedContent = z.object({
  _type: z.literal('featuredContent'),
  _key: z.string(),
  href: z.string(),
  headline: z.string().optional().nullable(),
  excerpt: z.string().optional().nullable(),
  thumbnailImage: SanityImage.optional().nullable(),
  referenceTitle: z.string(),
  referenceThumbnailImage: SanityImage,
  referenceExcerpt: z.string(),
  referenceType: z.enum(['post', 'caseStudy', 'resource']),
  cardBadge: z.string().optional().nullable(),
  ctaLabel: z.string().optional().nullable(),
});
export type SanityMenuFeaturedContent = z.infer<
  typeof SanityMenuFeaturedContent
>;

export const SanityMenuFeaturedLink = z.object({
  _type: z.literal('featuredLink'),
  _key: z.string(),
  title: z.string(),
  description: z.string(),
  link: SanityLink,
});
export type SanityMenuFeaturedLink = z.infer<typeof SanityMenuFeaturedLink>;

export const SanityMenuDivider = z.object({
  _type: z.literal('divider'),
  _key: z.string(),
});
export type SanityMenuDivider = z.infer<typeof SanityMenuDivider>;

export const SanityMenu = z.object({
  _type: z.literal('menu'),
  _key: z.string(),
  title: z.string(),
  columns: z.array(
    z.object({
      _key: z.string(),
      hasDivider: z.boolean(),
      background: z.string().optional().nullable(),
      textLink: SanityLink.optional().nullable(),
      sections: z.array(
        z.union([
          SanityMenuLinkGroup,
          SanityMenuFeaturedContent,
          SanityMenuFeaturedLink,
          SanityMenuDivider,
        ]),
      ),
    }),
  ),
});
export type SanityMenu = z.infer<typeof SanityMenu>;

export const SanityHeader = z.object({
  primaryNavigation: z.array(z.union([SanityLink, SanityMenu])),
  secondaryNavigation: z.array(SanityLink),
});
export type SanityHeader = z.infer<typeof SanityHeader>;
export const header = groq`{
  primaryNavigation[] {
    _type,
    _key,
    _type == 'interalLink' => ${link},
    _type == 'externalLink' => ${link},
    _type == 'menu' => {
      title,
      columns[] {
        _key,
        hasDivider,
        background,
        'textLink': textLink[0] ${link},
        sections[] {
          _type,
          _key,
          _type == 'linkGroup' => {
            title,
            // Drop broken icons (image object with no asset) so Zod optional
            // SanityImage doesn't reject the whole header.
            'icon': select(defined(icon.asset) => icon ${image}, null),
            'libraryIcon': libraryIcon-> {
              _type,
              title,
              svg,
            },
            description,
            items[] {
              _key,
              link[0] ${link},
              description,
              'style': coalesce(style, 'default'),
              'icon': select(defined(icon.asset) => icon ${image}, null),
            },
          },
          _type == 'featuredContent' => {
            ...(reference-> {
              'referenceTitle': title,
              'referenceThumbnailImage': thumbnailImage ${image},
              'referenceExcerpt': excerpt,
              'referenceType': _type,
              _type == 'post' => {
                'href': '/blog/' + slug.current,
                'cardBadge': category[0]->title,
              },
              _type == 'caseStudy' => {
                'href': '/case-studies/' + slug.current,
                'cardBadge': category[0]->title,
              },
              _type == 'resource' => {
                'href': '/resources/' + slug.current,
                'cardBadge': category->title,
              },
            }),
            thumbnailImage ${image},
            headline,
            excerpt,
            ctaLabel,
          },
          _type == 'featuredLink' => {
            title,
            description,
            'link': link[0] ${link},
          },
        },
      },
    },
  },
  secondaryNavigation[] ${link},
}`;
